import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { useBreakpoint } from "../WidgetBreakpoint";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";

import BarChart from "./BarChart";

export default function Widget(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  // Include the widget providers
  const breakpoints = useBreakpoint();

  const [chartSelection, setChartSelection] = useState("A");
  const [feedback, setFeedback] = useState(null);
  const [open, setOpen] = React.useState(false);
  const [choice, setChoice] = React.useState("Select answer");
  const [display, setDisplay] = useState("histogram");
  const [loaded, setLoaded] = useState();
  // const anchorRef = React.useRef(null);
  const [widgetArea] = useState({
    height: 0,
    width: 0,
  });

  const selected = {
    borderBottom: "3px solid #ffaa27",
    color: "white",
    background: "transparent",
    padding: "8px",
    width: "50%",
    textAlign: "center",
    cursor: "pointer",
  };

  const notSelected = {
    borderBottom: "1px solid #ffaa27",
    width: "50%",
    background: "transparent",
    color: "white",
    padding: "8px",
    textAlign: "center",
    cursor: "pointer",
  };

  useEffect(() => {
    import(`./locales/${props.i18next.language}/translation.json`).then(
      (module) => {
        props.i18next.addResourceBundle(
          "en",
          "translation",
          module.default,
          true,
          true
        );
        setLoaded(true);
      }
    );
  }, []);

  const handleToggle = async () => {
    if (feedback === "correct") {
      return;
    }
    await setOpen(!open);
    if (open === false) {
      document.getElementById("option-0").focus();
    }
    scrollToSubmit();
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleChoice = (val) => {
    if (feedback === "correct") {
      return;
    }
    setFeedback(null);
    setChoice(val);
  };

  const handleAnswer = () => {
    if (
      choice === "Select answer" ||
      feedback === "correct" ||
      feedback === "incorrect"
    ) {
      return;
    }
    if (choice === "Friend E") {
      setFeedback("correct");
    } else {
      setFeedback("incorrect");
    }
    scrollToSubmit();
  };

  const options = ["Friend A", "Friend B", "Friend C", "Friend D", "Friend E"];

  const scrollToSubmit = () => {
    setTimeout(() => {
      const objDiv = document.getElementById("feedback");
      objDiv.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const handleEnter = () => {
    if (event.keyCode === 13) {
      document.activeElement.click();
    }
  };

  const graphDescription = (tab) => {
    if (tab === "A") {
      return t(
        "Option A shows the histogram for the 20 sunflowers grown by your first friend, relative to the expected 3 meter average height. This histogram has two modes, at approximately 3.75 meters and 4.75 meters. The histogram shows that 5 sunflowers are shorter than 3 meters and 15 sunflowers are taller than 3 meters."
      );
    }
    if (tab === "B") {
      return t(
        "Option B shows the histogram for the 20 sunflowers grown by your second friend, relative to the expected 3 meter average height. This histogram has a mode at approximately 2.25 meters. The histogram shows that 18 sunflowers are shorter than 3 meters and 2 sunflowers are taller than 3 meters."
      );
    }
    if (tab === "C") {
      return t(
        "Option C shows the histogram for the 20 sunflowers grown by your third friend, relative to the expected 3 meter average height. This histogram has a mode at approximately 1.75 meters. The histogram shows that 16 sunflowers are shorter than 3 meters and 4 sunflowers are taller than 3 meters."
      );
    }
    if (tab === "D") {
      return t(
        "Option D shows the histogram for the 20 sunflowers grown by your fourth friend, relative to the expected 3 meter average height. This histogram has a mode at approximately 2.25 meters. The histogram shows that 13 sunflowers are shorter than 3 meters and 7 sunflowers are taller than 3 meters."
      );
    }
    if (tab === "E") {
      return t(
        "Option E shows the histogram for the 20 sunflowers grown by your fifth friend, relative to the expected 3 meter average height. This histogram has a mode at approximately 3.5 meters. The histogram shows that 9 sunflowers are shorter than 3 meters and 11 sunflowers are taller than 3 meters."
      );
    }
  };

  const setDisplayTab = (tab) => {
    setDisplay(tab);
  };

  return (
    <>
      <div
        style={{
          display: props.intro === "" ? "none" : null,
          border: "1px solid white",
          borderRadius: "5px",
          padding: "20px",
          color: "white",
          margin: "20px",
        }}
      >
        {parse(t(props.intro))}
      </div>
      <div
        className="widgetWrapper"
        style={{
          width: "100%",
          overflow: "hidden",
          display: loaded ? "flex" : "none",
          flexDirection: "column",
        }}
      >
        <div
          className="altTxt field"
          aria-label={t(
            "Histograms showing the heights and frequencies for the 20 sunflowers are shown. The x axis plots the heights of the sunflowers in meters, and the y axis plots their frequency or count. The mean line is at 3 meters. As you tab from A to E the histogram for each friend's sunflowers appears. To find out which group of sunflowers most closely match the expected 3 meter average, we can determine where the histogram's middle lies or where the mode lies. Because these are small datasets we can also count the number of sunflowers in each group that are shorter or taller than 3 meters."
          )}
          style={{
            height: "0px",
            overflow: "hidden",
            // font: "none",
          }}
        >
          {t(
            "Histograms showing the heights and frequencies for the 20 sunflowers are shown. The x axis plots the heights of the sunflowers in meters, and the y axis plots their frequency or count. The mean line is at 3 meters. As you tab from A to E the histogram for each friend's sunflowers appears. To find out which group of sunflowers most closely match the expected 3 meter average, we can determine where the histogram's middle lies or where the mode lies. Because these are small datasets we can also count the number of sunflowers in each group that are shorter or taller than 3 meters."
          )}
        </div>
        <div
          className="chart-type-select"
          style={{
            maxWidth: "1000px",
            margin: "auto",
            display: widgetArea.height < 678 ? "flex" : "none",
            flexDirection: "row",
            boxSizing: "border-box",
            width: "calc(100% - 20px)",
          }}
        >
          <div
            style={chartSelection == "A" ? selected : notSelected}
            onClick={() => setChartSelection("A")}
            tabIndex="0"
            role="button"
            aria-label={
              chartSelection == "A"
                ? `Histogram for friend A. Selected. ${graphDescription("A")}`
                : `Histogram for friend A. Unselected. ${graphDescription("A")}`
            }
            onKeyPress={() => handleEnter(event)}
          >
            {t("A")}
          </div>
          <div
            style={chartSelection == "B" ? selected : notSelected}
            onClick={() => setChartSelection("B")}
            tabIndex="0"
            role="button"
            aria-label={
              chartSelection == "B"
                ? `Histogram for friend B. Selected. ${graphDescription("B")}`
                : `Histogram for friend B. Unselected. ${graphDescription("B")}`
            }
            onKeyPress={() => handleEnter(event)}
          >
            {t("B")}
          </div>
          <div
            style={chartSelection == "C" ? selected : notSelected}
            onClick={() => setChartSelection("C")}
            tabIndex="0"
            role="button"
            aria-label={
              chartSelection == "C"
                ? `Histogram for friend C. Selected. ${graphDescription("C")}`
                : `Histogram for friend C. Unselected. ${graphDescription("C")}`
            }
            onKeyPress={() => handleEnter(event)}
          >
            {t("C")}
          </div>
          <div
            style={chartSelection == "D" ? selected : notSelected}
            onClick={() => setChartSelection("D")}
            tabIndex="0"
            role="button"
            aria-label={
              chartSelection == "D"
                ? `Histogram for friend D. Selected. ${graphDescription("D")}`
                : `Histogram for friend D. Unselected. ${graphDescription("D")}`
            }
            onKeyPress={() => handleEnter(event)}
          >
            {t("D")}
          </div>
          <div
            style={chartSelection == "E" ? selected : notSelected}
            onClick={() => setChartSelection("E")}
            tabIndex="0"
            role="button"
            aria-label={
              chartSelection == "E"
                ? `Histogram for friend E. Selected. ${graphDescription("E")}`
                : `Histogram for friend E. Unselected. ${graphDescription("E")}`
            }
            onKeyPress={() => handleEnter(event)}
          >
            {t("E")}
          </div>
        </div>
        <div
          // ref={widget.widgetArea}
          className="widgetCharts"
          style={{
            position: "relative",
            display: "flex",
            width: breakpoints.md ? "calc(100% - 32px)" : "calc(100% - 96px)",
            maxWidth: "1000px",
            height: "calc(100% - 32px)",
            padding: breakpoints.md ? "16px" : "32px",
            overflowY: "hidden",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "space-around",
            margin: "0 auto",
            // border: "1px solid red",
          }}
        >
          <BarChart
            width={
              breakpoints.xs
                ? 200
                : breakpoints.sm
                  ? 250
                  : breakpoints.md
                    ? 500
                    : 500
            }
            height={
              breakpoints.xs
                ? 250
                : breakpoints.sm
                  ? 250
                  : breakpoints.md
                    ? 300
                    : 300
            }
            widgetData={props.widgetData}
            hist={chartSelection}
            visible={props.visible}
            selectedGraph={chartSelection}
            display={display}
            setDisplay={setDisplayTab}
          />
        </div>
        <ClickAwayListener onClickAway={handleClose}>
          <div style={{ width: "225px", margin: "auto" }}>
            <div
              style={{
                display: open === true ? "flex" : "none",
                flexDirection: "column",
                justifyContent: "flex-end",
                backgroundColor: "white",
                borderRadius: "5px",
                width: "225px",
                margin: "auto",
                paddingBottom: "15px",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  padding: "15px",
                  fontWeight: "bold",
                }}
              >
                {t("Select answer")}
              </div>
              {options.map((option, key) => (
                <div
                  onClick={() => handleChoice(option)}
                  tabIndex="0"
                  role="button"
                  key={key}
                  aria-label={
                    key === options.indexOf(choice)
                      ? `${key + 1} ${option} selected`
                      : `${key + 1} ${option} unselected`
                  }
                  onKeyPress={() => handleEnter(event)}
                  id={`option-${key}`}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    maxWidth: "100%",
                    cursor: "pointer",
                    padding: "5px 15px",
                    margin: 0,
                    height: "38px",
                  }}
                >
                  <p
                    style={{
                      border: "1px solid black",
                      borderRadius: "15px",
                      padding: "3px",
                      width: "15px",
                      height: "15px",
                      textAlign: "center",
                      lineHeight: "15px",
                      margin: "auto 0",
                      backgroundColor:
                        key === options.indexOf(choice) ? "#003e6b" : null,
                      color: key === options.indexOf(choice) ? "white" : null,
                      // color: ,
                    }}
                  >
                    {key + 1}
                  </p>
                  <p
                    style={{
                      flex: "0 1 auto",
                      lineHeight: "38px",
                      margin: "auto 20px",
                      // color: ,
                    }}
                  >
                    {t(option)}
                  </p>
                </div>
              ))}
            </div>
            <div
              style={{
                display: open === true ? "flex" : "none",
                width: 0,
                height: 0,
                borderLeft: "12px solid transparent",
                borderRight: "12px solid transparent",
                borderTop: "12px solid white",
                margin: "auto",
                marginTop: "-2px",
              }}
            ></div>
            <div
              style={{
                maxWidth: "1000px",
                margin: "0px auto 0",
                display: "flex",
                flexDirection: "row",
                position: "relative",
                justifyContent: "space-around",
                alignItems: "center",
                minHeight: "50px",
              }}
            >
              <div>
                <button
                  style={{
                    cursor: "pointer",
                    border: "none",
                    width: "100px",
                    height: "38px",
                    borderRadius: "5px",
                    backgroundColor: "white",
                    marginLeft: "5px",
                    fontSize: "14px",
                    color:
                      feedback === "correct"
                        ? "#84be60"
                        : feedback === "incorrect"
                          ? "#ed3431"
                          : "black",
                    opacity: open ? 0.9 : null,
                  }}
                  onClick={handleToggle}
                >
                  {t(choice)}
                </button>
              </div>
              <button
                style={{
                  cursor:
                    choice === "Select answer" ||
                    feedback === "correct" ||
                    feedback === "incorrect"
                      ? "not-allowed"
                      : "pointer",
                  border: "none",
                  width: "100px",
                  height: "38px",
                  borderRadius: "5px",
                  backgroundColor: "#ffaa27",
                  color: "#013a64",
                  marginLeft: "5px",
                  fontSize: "14px",
                  opacity:
                    choice === "Select answer" || feedback === "correct"
                      ? 0.5
                      : 1,
                }}
                onClick={handleAnswer}
              >
                {t("Submit")}
              </button>
            </div>
          </div>
        </ClickAwayListener>
        <p
          id="feedback"
          // tabIndex="0"
          style={{
            color: feedback === "correct" ? "#84be60" : "#ed3431",
            width: "80%",
            margin: "20px auto",
            textAlign: "center",
          }}
        >
          {feedback === "correct"
            ? t(
                "Correct! Roughly half of the sunflowers lie on either side of the 3 m dotted line."
              )
            : feedback === "incorrect"
              ? t(
                  "Not quite. Hint: Where does the middle of the distribution lie? Try counting how many sunflowers lie on either side of the 3 m dotted line."
                )
              : null}
        </p>
      </div>
    </>
  );
}

Widget.propTypes = {
  widgetData: PropTypes.object,
  inline: PropTypes.boolean,
  visible: PropTypes.boolean,
  usersnap: PropTypes.string,
};
